import { CSSProperties } from 'react'
import styled, { css } from 'styled-components'

import { Spacings } from '@/theme/defaults'
import { withBorder } from '@/theme/utils'

import { FieldState } from '../text-input'
import { TextAreaLabelPosition, TextAreaResize, TextAreaSize } from './text-area.types'

export const getTextAreaState = ({
  isDisabled,
  isReadOnly,
  hasError,
}: {
  isDisabled: boolean
  isReadOnly: boolean
  hasError: boolean
}): FieldState => {
  if (isDisabled) return 'disabled'
  if (isReadOnly) return 'readOnly'
  if (hasError) return 'error'
  return 'default'
}

const SIZE_TO_GAP_MAP: Record<TextAreaSize, keyof Spacings> = {
  large: 12,
  medium: 8,
  small: 4,
}

const FONT_SIZE_MAP: Record<TextAreaSize, string> = {
  large: '1.6rem',
  medium: '1.4rem',
  small: '1.2rem',
}

const LINE_HEIGHT_MAP: Record<TextAreaSize, string> = {
  large: '2.4rem',
  medium: '2rem',
  small: '1.6rem',
}

const MIN_HEIGHT_MAP: Record<TextAreaSize, CSSProperties['minHeight']> = {
  large: '12rem',
  medium: '11.6rem',
  small: '9.6rem',
}

export const Container = styled.div<{
  $labelPosition: TextAreaLabelPosition
  $size: TextAreaSize
}>(({ theme, $labelPosition, $size }) => ({
  alignItems: $labelPosition === 'left' ? 'baseline' : 'stretch',
  display: 'flex',
  flexDirection: $labelPosition === 'left' ? 'row' : 'column',
  gap: $labelPosition === 'left' ? theme.space['16'] : theme.space[SIZE_TO_GAP_MAP[$size]],
  position: 'relative',
  width: '100%',
}))

// Note: holds the textarea and the footer (helper/error + counter) as one column.
export const Field = styled.div<{ $size: TextAreaSize }>(({ theme, $size }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space[SIZE_TO_GAP_MAP[$size]],
  width: '100%',
}))

export const TextArea = styled.textarea<{
  $hasError: boolean
  $isDisabled: boolean
  $isReadOnly: boolean
  $resize: TextAreaResize
  $size: TextAreaSize
}>(({ theme, $hasError, $isDisabled, $isReadOnly, $resize, $size }) => {
  const state = getTextAreaState({
    hasError: $hasError,
    isDisabled: $isDisabled,
    isReadOnly: $isReadOnly,
  })

  const backgroundMap: Record<FieldState, CSSProperties['background']> = {
    default: 'transparent',
    disabled: theme.colors.gray200,
    error: theme.colors.red100,
    readOnly: 'transparent',
  }

  const borderColorMap: Record<FieldState, CSSProperties['borderColor']> = {
    default: theme.colors.gray500,
    disabled: theme.colors.gray200,
    error: theme.colors.red200,
    readOnly: 'transparent',
  }

  const cursorMap: Record<FieldState, CSSProperties['cursor']> = {
    default: 'text',
    disabled: 'not-allowed',
    error: 'text',
    readOnly: 'default',
  }

  const readOnlyPaddingMap: Record<TextAreaSize, CSSProperties['padding']> = {
    large: `${theme.space['4']} 0`,
    medium: `${theme.space['4']} 0`,
    small: `${theme.space['4']} 0`,
  }

  const otherPaddingMap: Record<TextAreaSize, CSSProperties['padding']> = {
    large: `${withBorder(theme, 12)} ${withBorder(theme, 16)}`,
    medium: `${withBorder(theme, 12)} ${withBorder(theme, 12)}`,
    small: `${withBorder(theme, 8)} ${withBorder(theme, 12)}`,
  }

  const isInteractive = state === 'default' || state === 'error'

  return css`
    background: ${backgroundMap[state]};
    border: ${state === 'readOnly' ? 0 : theme.border.size['1']} solid ${borderColorMap[state]};
    border-radius: ${theme.border.radius['4']};
    color: ${$hasError ? theme.colors.red500 : theme.colors.primary800};
    cursor: ${cursorMap[state]};
    font-family: inherit;
    font-size: ${FONT_SIZE_MAP[$size]};
    line-height: ${LINE_HEIGHT_MAP[$size]};
    margin: 0;
    min-height: ${MIN_HEIGHT_MAP[$size]};
    outline: none;
    padding: ${state === 'readOnly' ? readOnlyPaddingMap[$size] : otherPaddingMap[$size]};
    resize: ${$resize};
    text-align: left;
    transition: border-color ${theme.transition.duration.sm} ease;
    width: 100%;

    &::placeholder {
      color: ${$hasError ? theme.colors.red400 : theme.colors.primary500};
    }

    // Note: hover/focus styling only applies to interactive (default/error) states.
    ${
      isInteractive &&
      css`
      &:focus {
        border-color: ${state === 'error' ? theme.colors.red500 : theme.colors.primary800};
      }

      @media (hover: hover) {
        &:hover:not(:focus) {
          border-color: ${state === 'error' ? theme.colors.red300 : theme.colors.primary400};
        }
      }
    `
    }

    // NOTE: by our design we have pretty UI-less styles for "read-only" state,
    // so, to make it ARIA compliant -> adding browser default visuals
    &:read-only:focus-visible {
      outline: -webkit-focus-ring-color auto ${theme.border.size['1']};
    }

    &:disabled {
      color: ${theme.colors.primary500};

      &::placeholder {
        color: ${theme.colors.primary400};
      }
    }
  `
})

// Note: footer holds the helper/error message and the optional character counter on one row.
export const Footer = styled.div<{ $size: TextAreaSize }>(({ theme, $size }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  fontSize: FONT_SIZE_MAP[$size],
  gap: theme.space['8'],
  lineHeight: LINE_HEIGHT_MAP[$size],
  minHeight: LINE_HEIGHT_MAP[$size],
}))

export const Message = styled.span<{ $hasError: boolean }>(({ theme, $hasError }) => ({
  color: $hasError ? theme.colors.red500 : theme.colors.primary600,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

export const Counter = styled.span<{ $isOverLimit: boolean }>(({ theme, $isOverLimit }) => ({
  color: $isOverLimit ? theme.colors.red500 : theme.colors.black500,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  marginLeft: 'auto',
}))

// NOTE: Preserve same number of digits for current count, so visually left and right columns have the same width
export const CurrentCount = styled.span<{ $digits: number }>(({ $digits }) => ({
  display: 'inline-block;',
  fontVariantNumeric: 'tabular-nums',
  minWidth: `${$digits}ch`,
  textAlign: 'right',
}))
